import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BusinessContext } from '../context/BusinessContext';
import { useContext } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import PublicBusinessProfile from './Business/PublicBusinessProfile';

function ProtectedRoute({ children, requireWedding = false }) {
  const { user, wedding, loading } = useAuth();
  const location = useLocation();
  const isSupplierRoute = location.pathname.startsWith('/supplier');
  const isAdminRoute = location.pathname.startsWith('/admin');
  const userType = localStorage.getItem('userType');
  const token = localStorage.getItem('token');
  const newUserRedirect = localStorage.getItem('newUserRedirect');
  
  // Get business context - will be null if not in BusinessProvider
  const businessContext = useContext(BusinessContext);
  const hasNoBusiness = businessContext?.hasNoBusiness;
  const businessLoading = businessContext?.loading;
  
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const [userTimeout, setUserTimeout] = useState(false);
  const [businessCheckTimeout, setBusinessCheckTimeout] = useState(false);
  
  // Set up safety timeout for loading
  useEffect(() => {
    let timer;
    if (loading) {
      // Create a timeout to handle stuck loading states
      timer = setTimeout(() => {
        setLoadingTimeout(true);
      }, 10000); // 10-second timeout
    }
    
    return () => {
      clearTimeout(timer);
    };
  }, [loading]);

  // Set up timeout for business check when user exists but business is loading
  useEffect(() => {
    let businessTimer;
    if (user && user.role === 'supplier' && isSupplierRoute && businessLoading && businessContext !== null) {
      // Give business check 5 seconds - if it takes longer, assume no business
      businessTimer = setTimeout(() => {
        setBusinessCheckTimeout(true);
      }, 5000);
    }
    
    return () => {
      if (businessTimer) clearTimeout(businessTimer);
    };
  }, [user, businessLoading, isSupplierRoute, businessContext]);

  // Set up a separate timeout specifically for user loading (skip for admin routes and no-access)
  useEffect(() => {
    let userTimer;
    // Skip timeout logic for /no-access route - it should render immediately
    if (location.pathname === '/no-access') {
      return;
    }
    
    // Reset timeout when user is loaded
    if (user) {
      setUserTimeout(false);
      return;
    }
    
    // If we have a token and loading is complete but no user, wait a bit more
    // Skip this timeout for admin routes since they handle their own authentication
    // For supplier routes, give more time since they need to load additional data
    if (token && !loading && !user && !userTimeout && !isAdminRoute) {
      // Check if this is a fresh login by checking if token was set recently
      const tokenSetTime = localStorage.getItem('tokenSetTime');
      const isFreshLogin = tokenSetTime && (Date.now() - parseInt(tokenSetTime)) < 5000; // Within last 5 seconds
      
      const timeoutDuration = isSupplierRoute 
        ? (isFreshLogin ? 10000 : 8000) // 10 seconds for fresh login, 8 for others
        : 5000; // 5 seconds for non-suppliers
      
      userTimer = setTimeout(() => {
        setUserTimeout(true);
      }, timeoutDuration);
    }
    
    return () => {
      if (userTimer) clearTimeout(userTimer);
    };
  }, [token, loading, user, userTimeout, isAdminRoute, isSupplierRoute, location.pathname]);

  // Check for newUserRedirect flag
  useEffect(() => {
    if (newUserRedirect === 'true') {
      // Clear the flag so we don't get stuck in a loop
      localStorage.removeItem('newUserRedirect');
      // Force redirect to no-access
      window.location.href = '/no-access';
    }
  }, [newUserRedirect]);

  // CRITICAL: Check for WordPress URLs AFTER all hooks but before auth checks
  // This allows WordPress profile URLs to render without authentication
  const currentPath = location.pathname;
  const knownAppPaths = [
    '/dashboard', '/login', '/register', '/admin', '/supplier', '/no-access',
    '/create-wedding', '/auth', '/spotify-callback', '/forgot-password',
    '/reset-password', '/team-member-register',
    '/supplier-agreement', '/refund-policy', '/cancellation-policy',
    '/service-level-disclaimer', '/review-moderation-policy',
    '/profile' // React profile routes
  ];
  const isKnownAppPath = knownAppPaths.some(path => 
    currentPath === path || currentPath.startsWith(path + '/')
  );
  
  // Check WordPress proxy flags
  const isWordPressProxy = typeof window !== 'undefined' && 
    window.__WORDPRESS_PROXY__ === true;
  const hasBusinessId = typeof window !== 'undefined' && 
    !!window.__PROFILE_BUSINESS_ID__;
  
  // Check if path looks like WordPress profile (2+ hyphens, not a known app path)
  const looksLikeWordPressProfile = !isKnownAppPath && currentPath && 
    currentPath !== '/' &&
    /^\/[a-z0-9]+(-[a-z0-9]+){2,}\/?$/i.test(currentPath);
  
  // If this is a WordPress URL, render PublicBusinessProfile and skip all auth checks
  if ((isWordPressProxy && hasBusinessId) || looksLikeWordPressProfile) {
    return <PublicBusinessProfile />;
  }

  // Special handling for /no-access route - allow it to render even with expired sessions
  if (location.pathname === '/no-access') {
    // If we have an expired token (token exists but no user after timeout), clear it
    if (token && !user && !loading && userTimeout) {
      localStorage.removeItem('token');
      localStorage.removeItem('userType');
    }
    // Always allow /no-access to render - it handles its own authentication checks
    return children;
  }


  // Show loading state while auth is being initialized
  if (loading && !loadingTimeout) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading your data...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This will only take a moment
        </Typography>
      </Box>
    );
  }

  // If we hit the loading timeout, show a message with a refresh button
  if (loadingTimeout) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6" sx={{ mb: 2 }}>
          Taking longer than expected...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          We're having trouble loading your data
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => window.location.reload()}
        >
          Refresh Page
        </Button>
      </Box>
    );
  }

  // If there's no token, redirect to login (except for /no-access)
  // WordPress URLs are already handled above and won't reach here
  if (!token && location.pathname !== '/no-access') {
    const loginPath = isSupplierRoute ? '/supplier/login' : 
                     isAdminRoute ? '/admin/login' : '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // For admin routes, validate admin access FIRST (before other user logic)
  if (isAdminRoute) {
    // If we have a user object and it's an admin, allow access
    if (user && user.role === 'admin') {
      return children;
    }

    // If we don't have a user object but have a token, something went wrong
    if (token && !user && !loading) {
      return <Navigate to="/admin/login" state={{ from: location }} replace />;
    }

    // Still loading, show loading screen
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Verifying admin access...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please wait while we validate your admin privileges
        </Typography>
      </Box>
    );
  }

  // If loading is done but we still don't have a user, show a temporary loading screen
  // while we wait for user data to load (skip for /no-access)
  if (!loading && token && !user && location.pathname !== '/no-access') {
    // For supplier routes, check if we should redirect to login after timeout
    if (isSupplierRoute && userType === 'supplier') {
      // If userTimeout is reached, redirect to login (likely due to 403 error)
      if (userTimeout) {
        return <Navigate to="/supplier/login" state={{ from: location }} replace />;
      }
      
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress size={40} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading supplier profile...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please wait while we load your supplier information and business data
          </Typography>
          {userTimeout && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              Taking longer than expected. If this continues, please refresh the page.
            </Typography>
          )}
        </Box>
      );
    }
    
    // Check if this is a new registration or login that needs redirection
    if (userTimeout) {
      return <Navigate to="/no-access" replace />;
    }
    
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Finalizing your login...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please wait while we load your information
        </Typography>
        
        {/* Only show refresh button if userTimeout is true */}
        {userTimeout && (
          <Button 
            variant="text" 
            color="primary"
            sx={{ mt: 3 }}
            onClick={() => {
              window.location.href = '/no-access';
            }}
          >
            Click here if nothing happens
          </Button>
        )}
      </Box>
    );
  }

  // For supplier routes, we still need to validate the user exists and is verified
  if (userType === 'supplier' && isSupplierRoute) {
    // If we have a user object, that means the backend validated the token and email verification
    if (user && user.role === 'supplier') {
      // Skip business check for registration routes to avoid redirect loops
      if (location.pathname.startsWith('/supplier/register')) {
        return children;
      }
      
      // If business context is available, check for no business
      if (businessContext !== null) {
        // If business check timed out, assume no business and redirect
        if (businessCheckTimeout) {
          return <Navigate to="/supplier/register" replace />;
        }
        
        // Check if user has no business - redirect to registration flow (step 2)
        if (hasNoBusiness && !businessLoading) {
          return <Navigate to="/supplier/register" replace />;
        }
        
        // If business is still loading, show loading screen (but with shorter timeout)
        if (businessLoading && !businessCheckTimeout) {
          return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
              <CircularProgress size={40} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Loading supplier profile...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please wait while we load your supplier information and business data
              </Typography>
            </Box>
          );
        }
      } else if (user && user.role === 'supplier' && !loading) {
        // Business context not available yet, but user is loaded - give it a moment
        // This handles the case where BusinessContext hasn't initialized yet
        return (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
            <CircularProgress size={40} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Loading supplier profile...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please wait while we load your supplier information and business data
            </Typography>
          </Box>
        );
      }
      
      return children;
    }
    
    // If we don't have a user object but have a token, something went wrong
    if (token && !user && !loading) {
      return <Navigate to="/supplier/login" state={{ from: location }} replace />;
    }
    
    // If no token, redirect to login
    if (!token) {
      return <Navigate to="/supplier/login" state={{ from: location }} replace />;
    }
    
    // Still loading, show loading screen
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Verifying supplier access...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please wait while we validate your account
        </Typography>
      </Box>
    );
  }

  
  // Redirect suppliers away from non-supplier routes
  if (userType === 'supplier' && !isSupplierRoute && location.pathname === '/dashboard') {
    return <Navigate to="/supplier/dashboard" replace />;
  }

  // Check wedding access requirement for non-supplier routes
  if (requireWedding && !wedding && location.pathname !== '/no-access' && 
      location.pathname !== '/create-wedding') {
    // Check if user needs to create a wedding or has pending invites
    const needsWedding = localStorage.getItem('needsWedding');
    const hasPendingInvites = localStorage.getItem('hasPendingInvites');
    
    if (needsWedding === 'true' || hasPendingInvites === 'true') {
      return <Navigate to="/no-access" replace />;
    }
    
    // If we don't have wedding data but no flags are set, redirect to no-access
    return <Navigate to="/no-access" replace />;
  }

  return children;
}

export default ProtectedRoute; 